import { CookieBanner } from '@/components/shared/CookieBanner';
import { ArrowRightIcon, CheckIcon, ShoppingBagIcon, CubeIcon, BuildingOfficeIcon, TruckIcon, DocumentTextIcon, CalculatorIcon, UserGroupIcon, ChatBubbleLeftRightIcon, SparklesIcon, TrophyIcon, BuildingLibraryIcon, BoltIcon, ShieldCheckIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { OpenMenu } from '@/components/shared/OpenMenu';
import LoginModal from '@/components/shared/LoginModal';
import SignUpModal from '@/components/shared/SignUpModal';
import { usePlanes } from '@/services/plataforma/usePlanes';
import { useTranslation } from 'react-i18next';

import iconoMacroindustrial from '@/assets/Icono-macroindustrial.png';
import logoMacroindustrial from '@/assets/logo-macroindustrial.png';

export function LandingPage() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const { t } = useTranslation('translation');

  const handleOpenLoginModal = () => {
    setIsLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  const handleOpenSignupModal = () => {
    setIsSignupModalOpen(true);
  };

  const handleCloseSignupModal = () => {
    setIsSignupModalOpen(false);
  };

  const features = [
    {
      icon: ShoppingBagIcon,
      title: t('landing.features.orders.title'),
      description: t('landing.features.orders.description'),
      color: 'from-orange-400 to-orange-600'
    },
    {
      icon: CubeIcon,
      title: t('landing.features.inventory.title'),
      description: t('landing.features.inventory.description'),
      color: 'from-slate-400 to-slate-600'
    },
    {
      icon: BuildingOfficeIcon,
      title: t('landing.features.production.title'),
      description: t('landing.features.production.description'),
      color: 'from-gray-400 to-gray-600'
    },
    {
      icon: TruckIcon,
      title: t('landing.features.suppliers.title'),
      description: t('landing.features.suppliers.description'),
      color: 'from-orange-500 to-orange-700'
    },
    {
      icon: DocumentTextIcon,
      title: t('landing.features.invoicing.title'),
      description: t('landing.features.invoicing.description'),
      color: 'from-orange-400 to-red-500'
    },
    {
      icon: CalculatorIcon,
      title: t('landing.features.accounting.title'),
      description: t('landing.features.accounting.description'),
      color: 'from-slate-500 to-slate-700'
    },
    {
      icon: UserGroupIcon,
      title: t('landing.features.crm.title'),
      description: t('landing.features.crm.description'),
      color: 'from-gray-500 to-gray-700'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: t('landing.features.customerService.title'),
      description: t('landing.features.customerService.description'),
      color: 'from-orange-600 to-orange-800'
    },
  ];

  const stats = [
    { number: t('landing.stats.companies.number'), label: t('landing.stats.companies.label'), sublabel: t('landing.stats.companies.sublabel') },
    { number: t('landing.stats.years.number'), label: t('landing.stats.years.label'), sublabel: t('landing.stats.years.sublabel') },
    { number: t('landing.stats.documents.number'), label: t('landing.stats.documents.label'), sublabel: t('landing.stats.documents.sublabel') },
  ];

  const benefits = [
    { icon: BoltIcon, title: t('landing.benefits.implementation.title'), description: t('landing.benefits.implementation.description') },
    { icon: ShieldCheckIcon, title: t('landing.benefits.security.title'), description: t('landing.benefits.security.description') },
    { icon: ArrowTrendingUpIcon, title: t('landing.benefits.growth.title'), description: t('landing.benefits.growth.description') },
  ];

  // Fetch plans from API
  const { data: plansData, loading, error } = usePlanes();

  // Map API data to component format with appropriate pricing display
  const plans = plansData?.map((plan, index) => ({
    name: plan.nombre,
    icon: index === 1 ? TrophyIcon : index === 0 ? SparklesIcon : BuildingLibraryIcon, // Default icons based on position
    price: plan.precioMensual === 0 ? 'Gratis' : `$${plan.precioMensual.toLocaleString('es-CO')}`,
    period: plan.precioMensual > 0 ? '/mes' : '',
    description: plan.descripcion,
    features: plan.caracteristicas.map(feat => {
      // Format each feature based on the API structure
      if (feat.precioUnitario === 0) {
        return `${feat.cantidad} ${feat.caracteristica}`;
      } else {
        return `${feat.cantidad} ${feat.caracteristica} - $${feat.precioUnitario.toLocaleString('es-CO')}`;
      }
    }),
    popular: plan.nombre.toLowerCase() === 'pyme' // Assuming PYME is the popular plan
  })) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-gray-200 dark:from-blue-900 dark:via-gray-900 dark:to-black dark:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] dark:from-blue-800 dark:via-blue-900 dark:to-black">
      <OpenMenu handleOpenModal={handleOpenLoginModal} handleOpenSignupModal={handleOpenSignupModal} />

      {/* Login Modal */}
      <LoginModal
        enableGoogle={true}
        isOpen={isLoginModalOpen}
        onClose={handleCloseLoginModal}
        hideTriggerButton
      />

      {/* Signup Modal */}
      <SignUpModal
        isOpen={isSignupModalOpen}
        enableFacebook={false}
        enableMicrosoft={false}

        onClose={handleCloseSignupModal}
        hideTriggerButton
      />

      <main>
        <section className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
            <div className="text-center max-w-4xl mx-auto">
              <div className="flex justify-center mb-8">
                <img
                  src={logoMacroindustrial}
                  alt="Macroindustrial"
                  className="object-contain rounded-2xl"
                />
              </div>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                {t('landing.hero.title')}
                <span className="block text-orange-600 dark:text-orange-400">
                  {t('landing.hero.subtitle')}
                </span>
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {t('landing.hero.description')}
              </p>
              <button
                onClick={handleOpenLoginModal}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-orange-500 text-white font-semibold text-lg rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                {t('landing.hero.ctaButton')}
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
                {t('landing.hero.trialText')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-orange-600 dark:text-orange-400 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{stat.label}</div>
                  <div className="text-gray-600 dark:text-gray-300 text-sm">{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="py-24 bg-gradient-to-b from-gray-50 via-gray-100 to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                {t('landing.features.title')}
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {t('landing.features.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="group p-8 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-400 hover:shadow-lg transition-all duration-300"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h4>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="benefits" className="py-24 bg-gradient-to-b from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                {t('landing.benefits.title')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="relative inline-flex items-center justify-center mb-6">
                      <div className="absolute inset-0 bg-orange-100 dark:bg-blue-900/30 rounded-full blur-xl" />
                      <div className="relative w-24 h-24 bg-orange-500 dark:bg-blue-600 rounded-full flex items-center justify-center">
                        <Icon className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">{benefit.title}</h4>
                    <p className="text-slate-600 dark:text-gray-300 leading-relaxed">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                {t('landing.pricing.title')}
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {t('landing.pricing.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {!loading && !error ? (
                plans.map((plan) => {
                  const Icon = plan.icon;
                  return (
                    <div
                      key={plan.name}
                      className={`relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                        plan.popular
                          ? 'border-orange-500 dark:border-orange-400 shadow-2xl shadow-orange-500/20 dark:shadow-orange-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-400'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-orange-500 text-white px-6 py-1.5 rounded-full text-sm font-bold shadow-lg dark:bg-orange-600">
                            {t('landing.pricing.popular')}
                          </span>
                        </div>
                      )}

                      <div className="p-8">
                        <div className="w-14 h-14 bg-orange-500 dark:bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                          <Icon className="w-7 h-7 text-white" />
                        </div>

                        <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h4>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">{plan.description}</p>

                        <div className="mb-8">
                          <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                            <span className="text-gray-600 dark:text-gray-300 text-lg">{plan.period}</span>
                          </div>
                        </div>

                        <div className="space-y-4 mb-8">
                          {plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <CheckIcon className="w-5 h-5 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={handleOpenSignupModal}
                          className={`w-full py-3.5 px-4 rounded-xl font-semibold transition-all ${
                            plan.popular
                              ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-md'
                              : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {t('landing.pricing.trialButton')}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="w-full text-center">
                  {loading ? (
                    <p className="text-gray-600 dark:text-gray-300">Cargando planes...</p>
                  ) : (
                    <p className="text-red-600 dark:text-red-400">Error al cargar los planes: {error}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-r from-orange-400 to-orange-500  dark:from-gray-800 dark:to-gray-900 rounded-2xl ">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('landing.cta.title')}
            </h3>
            <p className="text-xl text-orange-100 dark:text-blue-100 mb-10 leading-relaxed">
              {t('landing.cta.subtitle')}
            </p>
            <button
              onClick={handleOpenSignupModal}
              className="inline-flex items-center gap-3 px-10 py-4 bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 font-bold text-lg rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-2xl hover:scale-105"
            >
              {t('landing.cta.button')}
              <ArrowRightIcon className="w-6 h-6" />
            </button>
          </div>
        </section>

        <footer className="bg-gray-800 dark:bg-gradient-to-b dark:from-gray-900 dark:via-blue-900 dark:to-black text-gray-400 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <img
                    src={iconoMacroindustrial}
                    alt="Macroindustrial"
                    className="w-10 h-10 object-contain"
                  />
                  <h5 className="text-white font-bold text-lg dark:text-white">Macroindustrial ERP Pro</h5>
                </div>
                <p className="text-sm leading-relaxed dark:text-gray-300">
                  La solución completa para la gestión empresarial en Colombia.
                </p>
              </div>
              <div>
                <h5 className="text-white font-semibold mb-4 dark:text-gray-200">{t('landing.footer.product')}</h5>
                <ul className="space-y-3 text-sm">
                  <li><a href="#features" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.features')}</a></li>
                  <li><a href="#benefits" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.benefits')}</a></li>
                  <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.pricing')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.documentation')}</a></li>
                </ul>
              </div>
              <div>
                <h5 className="text-white font-semibold mb-4 dark:text-gray-200">{t('landing.footer.company')}</h5>
                <ul className="space-y-3 text-sm">
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.aboutUs')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.contact')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.blog')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.support')}</a></li>
                </ul>
              </div>
              <div>
                <h5 className="text-white font-semibold mb-4 dark:text-gray-200">{t('landing.footer.legal')}</h5>
                <ul className="space-y-3 text-sm">
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.terms')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.privacy')}</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors dark:text-gray-300 dark:hover:text-white">{t('landing.footer.law1581')}</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 dark:border-gray-700 pt-8 text-sm text-center">
              <p className="dark:text-gray-400">&copy; 2025 Macroindustrial ERP Pro. {t('landing.footer.copyright')}</p>
            </div>
          </div>
        </footer>
      </main>

      <CookieBanner />
    </div>
  );
}
